/**
 * Small helpers shared by the D5 screens (records, admin, platform, reports): org-keyed
 * queries, a mutation with toast + refresh, a responsive table (cards on phones), tabs,
 * and authenticated file downloads (certificates, report exports).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ErrorNote, EmptyState, Spinner, cx, useToast } from '../../../components/ui';
import { api, baseHeaders, getAccessToken, type RequestOptions } from '../../../lib/api';
import { useOrgKey } from '../../../lib/queries';

export function useGet<T>(key: unknown[], path: string | null, query?: RequestOptions['query'], opts: { refetchInterval?: number } = {}) {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, ...key, query ?? null], enabled: !!path, queryFn: () => api<T>(path!, { query }), refetchInterval: opts.refetchInterval });
}

/** POST/PUT/PATCH with a success toast; refreshes every query of the org afterwards. */
export function useAct<V = void, R = unknown>(fn: (v: V) => Promise<R>, success?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      if (success) toast.success(typeof success === 'function' ? success(r) : success);
      void qc.invalidateQueries();
    },
  });
}

export const post = <R = unknown,>(path: string, body: unknown = {}, method: RequestOptions['method'] = 'POST') => api<R>(path, { method, body });

export interface Col<T> {
  header: string;
  cell: (r: T) => ReactNode;
  align?: 'right';
  className?: string;
}

/** Plain table; below 640 px each row becomes a card (first column as its title). */
export function Table<T>({ rows, cols, rowKey, empty = 'Nothing here yet.', loading, error, caption }: { rows: T[] | undefined; cols: Col<T>[]; rowKey: (r: T) => string; empty?: string; loading?: boolean; error?: unknown; caption?: string }) {
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (loading || !rows) return <div className="p-6"><Spinner /></div>;
  if (!rows.length) return <EmptyState compact title={empty} />;
  return (
    <>
      <table className="hidden w-full text-sm sm:table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
            {cols.map((c) => (
              <th key={c.header} scope="col" className={cx('px-3 py-2 font-semibold', c.align === 'right' && 'text-right')}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} className="border-b border-line/60 align-top last:border-0">
              {cols.map((c) => (
                <td key={c.header} className={cx('px-3 py-2', c.align === 'right' && 'text-right tabular-nums', c.className)}>{c.cell(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="divide-y divide-line sm:hidden">
        {rows.map((r) => (
          <li key={rowKey(r)} className="p-3 text-sm">
            <div className="font-semibold">{cols[0].cell(r)}</div>
            <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {cols.slice(1).map((c) => (
                <div key={c.header} className="contents">
                  <dt className="text-muted">{c.header}</dt>
                  <dd className="min-w-0 break-words">{c.cell(r)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

export function Tabs<V extends string>({ value, onChange, tabs }: { value: V; onChange: (v: V) => void; tabs: { value: V; label: string; hidden?: boolean }[] }) {
  return (
    <div role="tablist" className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.value} role="tab" type="button" aria-selected={value === t.value} onClick={() => onChange(t.value)} className={cx('min-h-touch whitespace-nowrap border-b-2 px-3 text-sm font-semibold', value === t.value ? 'border-brand text-ink' : 'border-transparent text-muted hover:text-ink')}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Downloads a private API file (Bearer token) under `filename`. */
export async function download(path: string, filename: string, query?: RequestOptions['query']): Promise<void> {
  const qs = query ? `?${new URLSearchParams(Object.entries(query).filter(([, v]) => v !== undefined && v !== '' && v !== null).map(([k, v]) => [k, String(v)]))}` : '';
  const token = getAccessToken();
  const res = await fetch(`/api/v1${path}${qs}`, { credentials: 'include', headers: { ...baseHeaders(), ...(token ? { authorization: `Bearer ${token}` } : {}) } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '—');
export interface Named { id: string; name: string }
